import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionDto, HttpActionDto, PatchActionDto } from '@/Domain/api/Action/dto/Action.dto';
import {
  ActionTemplate,
  ActionTemplateCategory,
} from '@/Domain/components/Action/actionTemplate.types';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { HttpMethodEnum } from '@/Domain/enum/HttpMethod.enum';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

const HttpMethodBadge = ({ method }: { method: HttpMethodEnum }) => {
  const colorMap: Record<string, string> = {
    [HttpMethodEnum.POST]:
      'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
    [HttpMethodEnum.PATCH]: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
    [HttpMethodEnum.PUT]: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
    [HttpMethodEnum.GET]:
      'bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300',
    [HttpMethodEnum.DELETE]: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  };

  return (
    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${colorMap[method] ?? ''}`}>
      {method}
    </span>
  );
};

type TemplateCardProps = {
  testId: string;
  name: string;
  description: string;
  method: HttpMethodEnum;
  isLoading: boolean;
  onClick: () => void;
  disabled?: boolean;
};

const TemplateCard = ({
  testId,
  name,
  description,
  method,
  isLoading,
  onClick,
  disabled = false,
}: TemplateCardProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={isLoading || disabled}
    data-testid={testId}
    className="group w-full cursor-pointer rounded-xl border border-border bg-card p-4 text-left
               transition-all hover:border-foreground/20 hover:bg-accent/40 hover:shadow-sm
               focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40
               disabled:cursor-not-allowed disabled:opacity-50"
  >
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-sm text-foreground">{name}</span>
          <HttpMethodBadge method={method} />
        </div>
        <p className="text-xs text-foreground-subtle line-clamp-2">{description}</p>
      </div>
      <ArrowRight
        className="mt-0.5 ml-3 size-4 shrink-0 text-foreground-subtle transition-all
                   group-hover:translate-x-0.5 group-hover:text-foreground"
      />
    </div>
  </button>
);

type ActionTemplateGalleryItem = Pick<
  ActionTemplate,
  'id' | 'category' | 'nameKey' | 'descriptionKey' | 'method'
>;

export type ActionTemplateGalleryProps<
  TTemplate extends ActionTemplateGalleryItem = ActionTemplate,
> = {
  testIdPrefix: string;
  headerIcon: ReactNode;
  titleKey: string;
  descriptionKey: string;
  categories: ActionTemplateCategory[];
  templates: TTemplate[];
  scratchTemplate: ActionTemplate;
  scratchLabelKey: string;
  scratchDescriptionKey: string;
  actionIcon: string;
  actionIconColor: string;
  isConnected: boolean;
  isConnectionFetching?: boolean;
  connectionWarning?: ReactNode;
  templatesDisabled?: boolean;
  onTemplateSelect?: (template: TTemplate) => void | Promise<void>;
};

export const ActionTemplateGallery = <
  TTemplate extends ActionTemplateGalleryItem = ActionTemplate,
>({
  testIdPrefix,
  headerIcon,
  titleKey,
  descriptionKey,
  categories,
  templates,
  scratchTemplate,
  scratchLabelKey,
  scratchDescriptionKey,
  actionIcon,
  actionIconColor,
  isConnected,
  isConnectionFetching = false,
  connectionWarning,
  templatesDisabled = false,
  onTemplateSelect,
}: ActionTemplateGalleryProps<TTemplate>) => {
  const { t } = useTranslation('actions_page');
  const { closeModal, toggleModal } = useModalStore();
  const [loadingTemplateId, setLoadingTemplateId] = useState<string | null>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const createHttpRequestActionMutation = useMutation<HttpActionDto, unknown, void>({
    mutationKey: [QueryKey.actions.httpRequest.create],
  });

  const patchActionMutation = useMutation<ActionDto, unknown, PatchActionDto>({
    mutationKey: [QueryKey.actions.basic.patch],
  });

  const handleSelectTemplate = async (template: TTemplate | null) => {
    if (loadingTemplateId !== null) return;

    if (template && onTemplateSelect) {
      setLoadingTemplateId(template.id);
      try {
        await onTemplateSelect(template);
      } catch {
        return;
      } finally {
        setLoadingTemplateId(null);
      }
      return;
    }

    const selectedTemplate = (template as ActionTemplate | null) ?? scratchTemplate;
    setLoadingTemplateId(selectedTemplate.id);

    try {
      const httpRequestAction = await createHttpRequestActionMutation.mutateAsync();
      patchActionMutation.mutate({
        id: httpRequestAction.id,
        icon: actionIcon,
        icon_color: actionIconColor,
      });
      toggleModal(MODAL_NAMES.HTTP_REQUEST_ACTION_SHEET, {
        httpRequestAction,
        actionTemplate: selectedTemplate,
      });
    } catch {
      setLoadingTemplateId(null);
    }
  };

  return (
    <Dialog open onOpenChange={closeModal}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid={`${testIdPrefix}-template-gallery-dialog`}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          titleRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle
            ref={titleRef}
            tabIndex={-1}
            data-testid={`${testIdPrefix}-template-gallery-title`}
            className="flex items-center gap-2"
          >
            {headerIcon}
            {t(titleKey)}
          </DialogTitle>
          <DialogDescription>{t(descriptionKey)}</DialogDescription>
        </DialogHeader>

        {!isConnectionFetching && !isConnected && connectionWarning && (
          <div
            className="flex items-start gap-3 rounded-xl border border-warning/25 bg-warning-muted px-4 py-3.5"
            data-testid={`${testIdPrefix}-connection-warning`}
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />
            <div className="min-w-0 flex-1 text-sm text-warning-foreground">
              {connectionWarning}
            </div>
          </div>
        )}

        <div className="overflow-y-auto flex-1 space-y-6 pr-1 min-h-0">
          {categories.map((category) => {
            const categoryTemplates = templates.filter((tpl) => tpl.category === category.id);
            if (categoryTemplates.length === 0) return null;

            return (
              <div key={category.id}>
                <div className="flex items-center gap-2 mb-3">
                  <category.icon className="size-4 text-foreground-subtle" />
                  <h3
                    className="text-sm font-medium text-muted-foreground"
                    data-testid={`${testIdPrefix}-category-${category.id}`}
                  >
                    {t(category.labelKey)}
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {categoryTemplates.map((template) => (
                    <TemplateCard
                      key={template.id}
                      testId={`${testIdPrefix}-template-card-${template.id}`}
                      name={t(template.nameKey)}
                      description={t(template.descriptionKey)}
                      method={template.method}
                      isLoading={loadingTemplateId === template.id}
                      disabled={templatesDisabled}
                      onClick={() => handleSelectTemplate(template)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex flex-row !justify-between items-center border-t pt-4">
          <div className="flex-1">
            <p
              className="text-xs text-foreground-subtle"
              data-testid={`${testIdPrefix}-start-from-scratch-description`}
            >
              {t(scratchDescriptionKey)}
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => handleSelectTemplate(null)}
            disabled={loadingTemplateId !== null}
            data-testid={`${testIdPrefix}-start-from-scratch-button`}
          >
            {t(scratchLabelKey)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
